// src/components/Dashboard/Dashboard.jsx
import React, { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { supabase } from '../../lib/supabase'
import Layout from '../Layout/Layout'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'
import { aiService } from '../../services/aiService'
import { edgeFunctionService } from '../../services/edgeFunctionService'

const {
  FiPlus,
  FiClock,
  FiCheck,
  FiEdit,
  FiFileText,
  FiTrash2,
  FiUpload,
  FiAlertTriangle,
  FiLink,
  FiFolder,
} = FiIcons

const STIMULUS_BUCKET = 'stimulus'
const DASHBOARD_CATEGORY_KEY = 'dashboard_active_category'

const Dashboard = () => {
  const { user, loading: authLoading } = useAuth()
  const navigate = useNavigate()

  const [lessonDesigns, setLessonDesigns] = useState([])
  const [lessonPlans, setLessonPlans] = useState([])
  const [stimuli, setStimuli] = useState([])
  const [loading, setLoading] = useState(true)

  // Dashboard category selection
  // Default to 'stimuli' but hydrate from localStorage once user is known
  const [activeCategory, setActiveCategory] = useState('stimuli') // 'stimuli' | 'lessons' | 'completed'

  // Quick-start hero state
  const [qsSubject, setQsSubject] = useState('')
  const [qsMode, setQsMode] = useState('file') // 'file' | 'url' | 'text' (default PDF)
  const [qsContent, setQsContent] = useState('')
  const [qsUrl, setQsUrl] = useState('')
  const [qsUploadedFile, setQsUploadedFile] = useState(null)
  const [qsIsDragging, setQsIsDragging] = useState(false)
  const [qsProcessing, setQsProcessing] = useState(false)
  const [qsError, setQsError] = useState('')
  const [qsStimulus, setQsStimulus] = useState(null)
  const [qsThemes, setQsThemes] = useState([])
  const [qsSelectedThemes, setQsSelectedThemes] = useState([])
  const [qsStep, setQsStep] = useState('input') // 'input' | 'themes'
  const [isExtracting, setIsExtracting] = useState(false) // global UI lock during extract

  // Subject selection modal when generating lesson from an existing stimulus
  const [subjectModalOpen, setSubjectModalOpen] = useState(false)
  const [subjectModalStimulus, setSubjectModalStimulus] = useState(null)
  const [subjectModalSubject, setSubjectModalSubject] = useState('')
  const [subjectModalError, setSubjectModalError] = useState('')
  const [subjectModalLoading, setSubjectModalLoading] = useState(false)

  useEffect(() => {
    if (!authLoading && user) {
      fetchData()
    }
    if (!authLoading && !user) {
      setLoading(false)
    }
  }, [user, authLoading])

  useEffect(() => {
    if (!user) return
    if (typeof window === 'undefined') return

    try {
      const key = `${DASHBOARD_CATEGORY_KEY}:${user.id}`
      const saved = window.localStorage.getItem(key)

      if (saved === 'stimuli' || saved === 'lessons' || saved === 'completed') {
        setActiveCategory(saved)
      }
    } catch (e) {
      console.warn('Could not read dashboard category from localStorage', e)
    }
  }, [user])

  useEffect(() => {
    if (!user) return
    if (typeof window === 'undefined') return

    try {
      const key = `${DASHBOARD_CATEGORY_KEY}:${user.id}`
      window.localStorage.setItem(key, activeCategory)
    } catch (e) {
      console.warn('Could not save dashboard category to localStorage', e)
    }
  }, [activeCategory, user])

  const fetchData = async () => {
    setLoading(true)
    try {
      console.log('Fetching dashboard data')

      const [
        { data: designs, error: designsError, status: designsStatus },
        { data: plans, error: plansError, status: plansStatus },
        { data: stimuliData, error: stimuliError, status: stimuliStatus },
      ] = await Promise.all([
        supabase
          .from('lesson_designs')
          .select('*')
          .order('created_at', { ascending: false }),
        supabase
          .from('lesson_plans')
          .select('*')
          .eq('is_current', true)
          .order('created_at', { ascending: false }),
        supabase
          .from('stimuli')
          .select('*')
          .order('created_at', { ascending: false }),
      ])

      // lesson_designs
      if (designsError && designsStatus !== 300) {
        console.error('Error fetching lesson designs:', {
          status: designsStatus,
          error: designsError,
        })
      } else {
        setLessonDesigns(designs || [])
      }

      // lesson_plans
      if (plansError && plansStatus !== 300) {
        console.error('Error fetching lesson plans:', {
          status: plansStatus,
          error: plansError,
        })
      } else {
        setLessonPlans(plans || [])
      }

      // stimuli
      if (stimuliError && stimuliStatus !== 300) {
        console.error('Error fetching stimuli:', {
          status: stimuliStatus,
          error: stimuliError,
        })
      } else {
        setStimuli(stimuliData || [])
      }
    } catch (error) {
      console.error('Unexpected error fetching dashboard data:', error)
      setLessonDesigns([])
      setLessonPlans([])
      setStimuli([])
    } finally {
      setLoading(false)
    }
  }

  // ---------- Quick-start helpers ----------

  const handleQsFileSelect = (event) => {
    const file = event.target.files?.[0]
    if (!file) return
    setQsUploadedFile(file)
    setQsMode('file')
    setQsError('')
  }

  const handleQsDragOver = (event) => {
    event.preventDefault()
    event.stopPropagation()
    setQsIsDragging(true)
  }

  const handleQsDragLeave = (event) => {
    event.preventDefault()
    event.stopPropagation()
    setQsIsDragging(false)
  }

  const handleQsDrop = (event) => {
    event.preventDefault()
    event.stopPropagation()
    setQsIsDragging(false)
    const file = event.dataTransfer.files?.[0]
    if (!file) return
    setQsUploadedFile(file)
    setQsMode('file')
    setQsError('')
  }

  const toggleQsTheme = (theme) => {
    setQsSelectedThemes((prev) => {
      const exists = prev.find((t) => t.label === theme.label)
      if (exists) {
        return prev.filter((t) => t.label !== theme.label)
      }
      return [...prev, theme]
    })
  }

  const resetQuickStart = () => {
    setQsProcessing(false)
    setQsError('')
    setQsStimulus(null)
    setQsThemes([])
    setQsSelectedThemes([])
    setQsStep('input')
  }

  const handleQuickExtract = async () => {
    setQsError('')

    if (!qsSubject.trim()) {
      setQsError('Please select a subject.')
      return
    }

    const trimmedUrl = qsUrl.trim()
    const trimmedText = qsContent.trim()

    const isFileMode = qsMode === 'file'
    const isUrlMode = qsMode === 'url'
    const isTextMode = qsMode === 'text'

    const hasFile = !!qsUploadedFile
    const hasUrl = isUrlMode && trimmedUrl.length > 0
    const hasText = isTextMode && trimmedText.length >= 50

    // Mode-specific validation
    if (isFileMode && !hasFile) {
      setQsError(
        'Upload a PDF or image stimulus to extract lesson ideas.',
      )
      return
    }

    if (isUrlMode && !hasUrl) {
      setQsError('Paste a valid URL to your online stimulus.')
      return
    }

    if (isTextMode && !hasText) {
      setQsError(
        'Paste at least 50 characters of text to extract lesson ideas.',
      )
      return
    }

    if (!hasFile && !hasText && !hasUrl) {
      setQsError(
        'Upload a file, paste stimulus text, or add a URL to extract lesson ideas.',
      )
      return
    }

    setQsProcessing(true)
    setIsExtracting(true)

    try {
      // 1. Create stimulus row in DB
      const baseInsert = {
        title: 'Untitled stimulus',
        description: null,
        content: hasText ? trimmedText : null,
        url: hasUrl ? trimmedUrl : null,
        source_type: hasFile
          ? 'uploaded_file'
          : hasUrl
          ? 'url'
          : 'pasted_text',
        storage_bucket: hasFile ? STIMULUS_BUCKET : null,
        storage_object_path: null,
        original_filename: hasFile ? qsUploadedFile?.name ?? null : null,
        mime_type: hasFile ? qsUploadedFile?.type ?? null : null,
        file_size_bytes: hasFile ? qsUploadedFile?.size ?? null : null,
        created_by: user?.id ?? null,
      }

      const { data: stimulus, error: stimulusError } = await supabase
        .from('stimuli')
        .insert(baseInsert)
        .select()
        .single()

      if (stimulusError || !stimulus) {
        console.error('Error creating quick-start stimulus:', stimulusError)
        throw new Error('Could not save stimulus. Please try again.')
      }

      // 2. If there's a file, upload it to storage
      if (hasFile) {
        const ext =
          qsUploadedFile.name.split('.').pop()?.toLowerCase() || 'bin'
        const objectPath = `stimulus/${stimulus.id}/original.${ext}`

        const { error: uploadError } = await supabase.storage
          .from(STIMULUS_BUCKET)
          .upload(objectPath, qsUploadedFile, {
            upsert: true,
            contentType: qsUploadedFile.type || undefined,
          })

        if (uploadError) {
          console.error('Error uploading quick-start file:', uploadError)
          throw new Error('Could not upload file. Please try again.')
        }

        const { error: updateError } = await supabase
          .from('stimuli')
          .update({ storage_object_path: objectPath })
          .eq('id', stimulus.id)

        if (updateError) {
          console.error(
            'Error updating quick-start stimulus with storage path:',
            updateError,
          )
          throw new Error('Could not update file information. Please try again.')
        }
      }

      // 3. Extract text (handles file, pasted text, and URL cases)
      const result = await edgeFunctionService.extractStimulusText(stimulus.id)
      console.log('extractStimulusText result (quick start):', result)

      if (!result?.success && result?.error) {
        throw new Error(
          result.error ||
            'Failed to extract text from stimulus. Please try again.',
        )
      }

      // 4. Theme extraction by stimulusId
      const themes = await aiService.analyzeStimulusByStimulusId(
        stimulus.id,
        qsSubject,
      )

      if (!themes || themes.length === 0) {
        throw new Error(
          'No lesson ideas were generated. Try different content or a clearer subject.',
        )
      }

      setQsStimulus(stimulus)
      setQsThemes(themes)
      setQsSelectedThemes([])
      setQsStep('themes')
    } catch (err) {
      console.error('Quick-start extract error:', err)
      setQsError(
        err?.message ||
          'Something went wrong while extracting lesson ideas. Please try again.',
      )
    } finally {
      setQsProcessing(false)
      setIsExtracting(false)
    }
  }

  const handleQuickStartContinue = async () => {
    if (!qsStimulus) {
      setQsError('No stimulus found. Please start again.')
      return
    }
    if (qsSelectedThemes.length === 0) {
      setQsError('Please select at least one theme.')
      return
    }

    setQsProcessing(true)
    setQsError('')

    try {
      // 1. Persist themes
      const themeInserts = qsSelectedThemes.map((theme) => ({
        stimulus_id: qsStimulus.id,
        label: theme.label,
        description: theme.description,
      }))

      const { data: themes, error: themesError } = await supabase
        .from('stimulus_themes')
        .insert(themeInserts)
        .select()

      if (themesError) throw themesError

      // 2. Create lesson design
      const primaryThemeLabel = qsSelectedThemes[0]?.label || null

      const autoTitle =
        primaryThemeLabel && qsSubject
          ? `${qsSubject} – ${primaryThemeLabel}`
          : primaryThemeLabel || qsSubject || 'New lesson design'

      const { data: lessonDesign, error: lessonError } = await supabase
        .from('lesson_designs')
        .insert({
          stimulus_id: qsStimulus.id,
          subject: qsSubject,
          title: autoTitle,
          status: 'collecting_input',
        })
        .select()
        .single()

      if (lessonError) throw lessonError

      // 3. Link themes to lesson design
      const links = themes.map((t) => ({
        lesson_design_id: lessonDesign.id,
        stimulus_theme_id: t.id,
      }))

      const { error: linksError } = await supabase
        .from('lesson_design_themes')
        .insert(links)

      if (linksError) throw linksError

      navigate(`/lesson/${lessonDesign.id}`)
    } catch (error) {
      console.error('Quick-start continue error:', error)
      setQsError(
        error?.message ||
          'Failed to create a lesson from these themes. Please try again.',
      )
    } finally {
      setQsProcessing(false)
    }
  }

  // ---------- Subject modal helper for existing stimuli ----------

  const handleSubjectModalContinue = async () => {
    const subject = subjectModalSubject.trim()
    const stimulus = subjectModalStimulus

    if (!stimulus?.id) {
      setSubjectModalError('No stimulus selected. Please try again.')
      return
    }

    if (!subject) {
      setSubjectModalError('Please select a subject.')
      return
    }

    setSubjectModalError('')
    setSubjectModalLoading(true)

    // Reuse quick-start state so we can show the same themes UI
    setQsSubject(subject)
    setQsError('')
    setQsProcessing(true)

    try {
      // Theme extraction by existing stimulusId (no text re-extraction)
      const themes = await aiService.analyzeStimulusByStimulusId(
        stimulus.id,
        subject,
      )

      if (!themes || themes.length === 0) {
        throw new Error(
          'No lesson ideas were generated. Try a different subject or stimulus.',
        )
      }

      // Drive the existing "themes" step (same as quick-start)
      setQsStimulus(stimulus)
      setQsThemes(themes)
      setQsSelectedThemes([])
      setQsStep('themes')

      // Close modal
      setSubjectModalOpen(false)
      setSubjectModalStimulus(null)
    } catch (err) {
      console.error('Stimulus theme extraction error:', err)
      setSubjectModalError(
        err?.message ||
          'Something went wrong while extracting lesson ideas. Please try again.',
      )
    } finally {
      setSubjectModalLoading(false)
      setQsProcessing(false)
    }
  }

  // ---------- Existing helpers ----------

  const handleDeleteStimulus = async (stimulusId) => {
    const confirmed = window.confirm(
      'Delete this stimulus? This cannot be undone.',
    )
    if (!confirmed) return

    try {
      const { error } = await supabase
        .from('stimuli')
        .delete()
        .eq('id', stimulusId)

      if (error) {
        console.error('Error deleting stimulus:', error)
        alert('Could not delete this stimulus. Please try again.')
        return
      }

      setStimuli((prev) => prev.filter((s) => s.id !== stimulusId))
    } catch (err) {
      console.error('Unexpected error deleting stimulus:', err)
      alert('Unexpected error deleting stimulus.')
    }
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case 'collecting_input':
        return <SafeIcon icon={FiEdit} className="w-4 h-4 text-orange-500" />
      case 'plan_generated':
        return <SafeIcon icon={FiClock} className="w-4 h-4 text-blue-500" />
      case 'finalised':
        return <SafeIcon icon={FiCheck} className="w-4 h-4 text-green-500" />
      default:
        return null
    }
  }

  const getStatusText = (status) => {
    switch (status) {
      case 'collecting_input':
        return 'In Progress'
      case 'plan_generated':
        return 'Plan Generated'
      case 'finalised':
        return 'Finalised'
      default:
        return status
    }
  }

  const inProgressDesigns = lessonDesigns.filter(
    (d) => d.status === 'collecting_input',
  )

  const otherDesigns = lessonDesigns.filter(
    (d) => d.status !== 'collecting_input' && d.status !== 'finalised',
  )

  const combinedLessonsInProgress = [...inProgressDesigns, ...otherDesigns]

  if (loading || authLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-12">
          <div className="text-gray-500">Loading your lessons...</div>
        </div>
      </Layout>
    )
  }

  const hasAnyData =
    combinedLessonsInProgress.length > 0 ||
    lessonPlans.length > 0 ||
    stimuli.length > 0

  const categories = [
    {
      id: 'stimuli',
      label: 'Saved Stimuli',
      sublabel: 'Reuse stimuli to start new lessons',
      count: stimuli.length,
      iconBg: 'bg-pink-100 text-pink-600',
    },
    {
      id: 'lessons',
      label: 'Lessons in progress',
      sublabel: 'Draft designs and plans you can keep refining',
      count: combinedLessonsInProgress.length,
      iconBg: 'bg-blue-100 text-blue-600',
    },
    {
      id: 'completed',
      label: 'Completed lesson plans',
      sublabel: 'Finalised plans ready to teach from',
      count: lessonPlans.length,
      iconBg: 'bg-emerald-100 text-emerald-600',
    },
  ]

  return (
    <Layout>
      <div className="space-y-8">
        {/* Hero quick-start card */}
        <div className="bg-white border border-gray-100 rounded-3xl shadow-xl px-4 py-8 md:px-10 md:py-10 hover:shadow-2xl transition-shadow">
          {/* Title + subtitle */}
          <div className="text-center max-w-2xl mx-auto">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900">
              Get Started With A New Idea
            </h1>
            <p className="mt-2 text-sm md:text-base text-gray-600">
              Upload a PDF, or paste a URL or text, then extract lesson ideas in
              one click.
            </p>
          </div>

          {/* Subject selector */}
          <div className="mt-6 flex flex-col items-center space-y-2">
            <span className="text-xs font-semibold tracking-wide uppercase text-gray-500">
              Subject
            </span>
            <select
              value={qsSubject}
              onChange={(e) => setQsSubject(e.target.value)}
              className="w-full max-w-xs px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            >
              <option value="">Select a subject</option>
              <option value="English">English</option>
              <option value="Music">Music</option>
              <option value="Art">Art</option>
              <option value="History">History</option>
              <option value="Science">Science</option>
              <option value="Mathematics">Mathematics</option>
              <option value="Other">Other</option>
            </select>
          </div>

          {/* Tabs */}
          <div className="mt-8 flex justify-center">
            <div className="inline-flex rounded-2xl bg-gray-100 p-1 shadow-inner">
              <button
                type="button"
                onClick={() => setQsMode('file')}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                  qsMode === 'file'
                    ? 'bg-white shadow-sm text-gray-900'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <SafeIcon icon={FiUpload} className="w-4 h-4" />
                <span>Upload a PDF</span>
              </button>
              <button
                type="button"
                onClick={() => setQsMode('url')}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                  qsMode === 'url'
                    ? 'bg-white shadow-sm text-gray-900'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <SafeIcon icon={FiLink} className="w-4 h-4" />
                <span>Paste Stimulus URL</span>
              </button>
              <button
                type="button"
                onClick={() => setQsMode('text')}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                  qsMode === 'text'
                    ? 'bg-white shadow-sm text-gray-900'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <SafeIcon icon={FiFileText} className="w-4 h-4" />
                <span>Paste Stimulus Text</span>
              </button>
            </div>
          </div>

          {/* Tab content area */}
          <div className="mt-6">
            {qsMode === 'file' && (
              <div
                onDragOver={handleQsDragOver}
                onDragLeave={handleQsDragLeave}
                onDrop={handleQsDrop}
                className={`relative flex flex-col items-center justify-center px-4 py-10 border-2 border-dashed rounded-2xl text-center cursor-pointer transition-colors ${
                  qsIsDragging
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-300 hover:border-blue-400 bg-gray-50'
                }`}
                onClick={() => {
                  const input = document.getElementById('qs-file-input')
                  if (input) input.click()
                }}
              >
                <input
                  id="qs-file-input"
                  type="file"
                  accept=".pdf,image/*"
                  className="hidden"
                  onChange={handleQsFileSelect}
                />
                <SafeIcon
                  icon={FiUpload}
                  className="w-10 h-10 text-gray-400 mb-3"
                />
                <p className="text-sm text-gray-800">
                  Click to upload or drag and drop
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  PDF or image stimulus – max 50MB
                </p>

                {qsUploadedFile && (
                  <div className="mt-4 text-xs text-gray-700 bg-white border border-gray-200 rounded-lg px-3 py-2 w-full max-w-md text-left shadow-sm">
                    <div className="font-medium truncate">
                      Selected file: {qsUploadedFile.name}
                    </div>
                    <div className="text-gray-500 mt-1">
                      Type: {qsUploadedFile.type || 'Unknown'} • Size:{' '}
                      {Math.round(qsUploadedFile.size / 1024)} KB
                    </div>
                  </div>
                )}
              </div>
            )}

            {qsMode === 'url' && (
              <div className="max-w-2xl mx-auto mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
                  Stimulus URL
                </label>
                <input
                  type="url"
                  value={qsUrl}
                  onChange={(e) => setQsUrl(e.target.value)}
                  placeholder="Paste a web link to your article, poem, interview, etc…"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                />
                <p className="mt-2 text-xs text-gray-500">
                  We&apos;ll fetch the page, extract the main readable text, and
                  use it to generate lesson themes. Some sites may not be fully
                  supported.
                </p>
              </div>
            )}

            {qsMode === 'text' && (
              <div className="max-w-2xl mx-auto mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
                  Stimulus text
                </label>
                <textarea
                  rows={8}
                  value={qsContent}
                  onChange={(e) => setQsContent(e.target.value)}
                  placeholder="Paste your article, interview, transcript, or other stimulus text here…"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                />
                <p className="text-xs text-gray-500 mt-1">
                  At least 50 characters recommended for good theme extraction.
                </p>
              </div>
            )}
          </div>

          {/* Error + Extract button */}
          <div className="mt-6 space-y-3 max-w-2xl mx-auto">
            {qsError && qsStep === 'input' && (
              <div className="flex items-start space-x-2 text-sm bg-red-50 border border-red-100 text-red-700 px-3 py-2 rounded-md">
                <SafeIcon
                  icon={FiAlertTriangle}
                  className="w-4 h-4 mt-0.5 flex-shrink-0"
                />
                <span>{qsError}</span>
              </div>
            )}

            <div className="flex justify-center">
              <button
                type="button"
                onClick={handleQuickExtract}
                disabled={qsProcessing}
                className="inline-flex items-center justify-center px-8 py-3 rounded-xl bg-gradient-to-r from-blue-600 to-pink-500 text-white text-sm font-semibold shadow-sm hover:from-blue-700 hover:to-pink-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {qsProcessing ? (
                  <>
                    <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                    Extracting lesson ideas...
                  </>
                ) : (
                  <>
                    <SafeIcon icon={FiUpload} className="w-4 h-4 mr-2" />
                    Extract Lesson Ideas
                  </>
                )}
              </button>
            </div>

            <p className="text-xs text-gray-500 text-center">
              We’ll save your stimulus, extract the text, and generate suggested
              themes automatically.
            </p>
          </div>
        </div>

        {/* Theme selection area (shown after quick extract or from existing stimulus) */}
        {qsStep === 'themes' && qsThemes.length > 0 && (
          <div className="bg-white border border-gray-200 rounded-2xl shadow-sm px-4 py-6 md:px-8 md:py-8 space-y-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                Select learning themes
              </h2>
              <p className="text-sm text-gray-600">
                These lesson ideas were generated from your stimulus. Choose the
                ones you’d like to build a lesson around.
              </p>
            </div>

            <div className="space-y-3">
              {qsThemes.map((theme, index) => {
                const selected = qsSelectedThemes.find(
                  (t) => t.label === theme.label,
                )
                return (
                  <div
                    key={index}
                    onClick={() => toggleQsTheme(theme)}
                    className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                      selected
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-start space-x-3">
                      <div
                        className={`mt-1 w-4 h-4 rounded border flex items-center justify-center ${
                          selected
                            ? 'bg-blue-500 border-blue-500'
                            : 'border-gray-300'
                        }`}
                      >
                        {selected && (
                          <SafeIcon
                            icon={FiCheck}
                            className="w-3 h-3 text-white"
                          />
                        )}
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">
                          {theme.label}
                        </h3>
                        <p className="text-gray-600 text-sm mt-1">
                          {theme.description}
                        </p>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>

            {qsError && (
              <div className="flex items-start space-x-2 text-sm bg-red-50 border border-red-100 text-red-700 px-3 py-2 rounded-md">
                <SafeIcon
                  icon={FiAlertTriangle}
                  className="w-4 h-4 mt-0.5 flex-shrink-0"
                />
                <span>{qsError}</span>
              </div>
            )}

            <div className="flex flex-col sm:flex-row sm:justify-end gap-3">
              <button
                type="button"
                onClick={resetQuickStart}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50"
              >
                Start over
              </button>
              <button
                type="button"
                onClick={handleQuickStartContinue}
                disabled={qsProcessing || qsSelectedThemes.length === 0}
                className="px-6 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {qsProcessing ? 'Creating lesson…' : 'Continue with selected themes'}
              </button>
            </div>
          </div>
        )}

        {/* Empty state */}
        {!hasAnyData && (
          <div className="text-center py-10 text-sm text-gray-500">
            <p className="mb-2">
              You don’t have any lesson designs or saved stimuli yet.
            </p>
            <p>
              Use the area above to add a stimulus and extract lesson ideas to
              create your first lesson.
            </p>
          </div>
        )}

        {/* Category cards + filtered content */}
        {hasAnyData && (
          <div className="space-y-6">
            {/* Category cards */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-lg font-semibold text-gray-900">
                  Your work
                </h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {categories.map((cat) => {
                  const isActive = activeCategory === cat.id
                  return (
                    <button
                      key={cat.id}
                      type="button"
                      onClick={() => setActiveCategory(cat.id)}
                      className={`relative text-left rounded-2xl px-5 py-4 bg-white border shadow-sm transition-all hover:shadow-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
                        isActive ? 'border-blue-500' : 'border-gray-200'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div
                            className={`w-9 h-9 rounded-full flex items-center justify-center ${cat.iconBg}`}
                          >
                            <SafeIcon icon={FiFolder} className="w-4 h-4" />
                          </div>
                          <div>
                            <div className="text-sm font-semibold text-gray-900">
                              {cat.label}
                            </div>
                            <div className="text-xs text-gray-500">
                              {cat.sublabel}
                            </div>
                          </div>
                        </div>
                        <div className="text-xs font-semibold text-gray-500">
                          {cat.count} item{cat.count === 1 ? '' : 's'}
                        </div>
                      </div>
                    </button>
                  )
                })}
              </div>
            </div>

            {/* Category content */}
            {activeCategory === 'stimuli' && (
              <div className="space-y-3">
                <h3 className="text-md font-semibold text-gray-900">
                  Saved Stimuli
                </h3>
                <p className="text-sm text-gray-600">
                  Reuse a stimulus to create a new lesson design and plan.
                </p>

                {stimuli.length === 0 ? (
                  <p className="text-sm text-gray-500">
                    No saved stimuli yet. Use the quick-start area above to add one.
                  </p>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {stimuli.map((stimulus) => (
                      <div
                        key={stimulus.id}
                        className="bg-white p-4 rounded-2xl shadow-sm border border-gray-200 flex flex-col justify-between"
                      >
                        <div>
                          <div className="flex items-center space-x-2">
                            <SafeIcon
                              icon={FiFileText}
                              className="w-4 h-4 text-gray-500"
                            />
                            <h3 className="text-sm font-semibold text-gray-900 truncate">
                              {stimulus.title || 'Untitled stimulus'}
                            </h3>
                          </div>

                          <div className="flex flex-wrap items-center gap-2 mt-3 text-[11px] text-gray-500">
                            {stimulus.source_type && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-gray-100 text-gray-700">
                                {stimulus.source_type === 'uploaded_file'
                                  ? 'Uploaded file'
                                  : stimulus.source_type === 'pasted_text'
                                  ? 'Pasted text'
                                  : stimulus.source_type === 'url'
                                  ? 'URL'
                                  : stimulus.source_type}
                              </span>
                            )}
                            {stimulus.mime_type && (
                              <span>{stimulus.mime_type}</span>
                            )}
                            {stimulus.created_at && (
                              <span>
                                Created:{' '}
                                {new Date(
                                  stimulus.created_at,
                                ).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="mt-4 flex items-center justify-between gap-2">
                          <button
                            type="button"
                            onClick={() => handleDeleteStimulus(stimulus.id)}
                            className="inline-flex items-center px-2 py-1 bg-red-50 text-red-700 rounded-lg hover:bg-red-100 transition-colors text-xs"
                          >
                            <SafeIcon
                              icon={FiTrash2}
                              className="w-3 h-3 mr-1"
                            />
                            Delete
                          </button>
                          <div className="flex gap-2">
                            <Link
                              to={`/stimulus/${stimulus.id}`}
                              className="px-2 py-1 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-xs"
                            >
                              View
                            </Link>
                            <button
                              type="button"
                              onClick={() => {
                                setSubjectModalStimulus(stimulus)
                                // Pre-fill with hero subject if set
                                setSubjectModalSubject(qsSubject || '')
                                setSubjectModalError('')
                                setSubjectModalOpen(true)
                              }}
                              className="px-2 py-1 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors text-xs"
                            >
                              Generate new lesson
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeCategory === 'lessons' && (
              <div className="space-y-3">
                <h3 className="text-md font-semibold text-gray-900">
                  Lessons in progress
                </h3>
                <p className="text-sm text-gray-600">
                  These designs and plans are still in progress. Click “View” to
                  keep working on them.
                </p>

                {combinedLessonsInProgress.length === 0 ? (
                  <p className="text-sm text-gray-500">
                    No lessons in progress yet. Generate a lesson from a
                    stimulus above to get started.
                  </p>
                ) : (
                  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {combinedLessonsInProgress.map((design) => (
                      <div
                        key={design.id}
                        className="bg-white p-6 rounded-lg shadow-sm border border-gray-200"
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <h4 className="text-base font-semibold text-gray-900">
                              {design.subject}{' '}
                              {design.year_level && `- ${design.year_level}`}
                            </h4>

                            <div className="flex flex-wrap items-center gap-3 mt-3 text-xs text-gray-500">
                              {design.lesson_duration_minutes && (
                                <span>
                                  Duration: {design.lesson_duration_minutes}{' '}
                                  minutes
                                </span>
                              )}
                              {design.is_series && design.lesson_count && (
                                <span>
                                  Series: {design.lesson_count} lessons
                                </span>
                              )}
                              {design.created_at && (
                                <span>
                                  Created:{' '}
                                  {new Date(
                                    design.created_at,
                                  ).toLocaleDateString()}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center space-x-3 ml-4">
                            <div className="flex items-center space-x-2">
                              {getStatusIcon(design.status)}
                              <span className="text-xs font-medium text-gray-700">
                                {getStatusText(design.status)}
                              </span>
                            </div>
                            <Link
                              to={`/lesson/${design.id}`}
                              className="px-3 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors text-xs"
                            >
                              View
                            </Link>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeCategory === 'completed' && (
              <div className="space-y-3">
                <h3 className="text-md font-semibold text-gray-900">
                  Completed lesson plans
                </h3>
                <p className="text-sm text-gray-600">
                  These are the latest versions of your generated plans.
                </p>

                {lessonPlans.length === 0 ? (
                  <p className="text-sm text-gray-500">
                    No completed lesson plans yet. Finish a lesson design to
                    generate a plan.
                  </p>
                ) : (
                  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {lessonPlans.map((plan) => (
                      <div
                        key={plan.id}
                        className="bg-white p-6 rounded-lg shadow-sm border border-gray-200"
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <h4 className="text-base font-semibold text-gray-900">
                              {plan.title || 'Lesson plan'}
                            </h4>
                            <div className="flex flex-wrap items-center gap-3 mt-3 text-xs text-gray-500">
                              {plan.estimated_total_minutes && (
                                <span>
                                  Estimated time:{' '}
                                  {plan.estimated_total_minutes} minutes
                                </span>
                              )}
                              <span>
                                Version: {plan.version} • Created:{' '}
                                {new Date(
                                  plan.created_at,
                                ).toLocaleDateString()}
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center space-x-3 ml-4">
                            <div className="flex items-center space-x-2">
                              <SafeIcon
                                icon={FiCheck}
                                className="w-4 h-4 text-green-500"
                              />
                              <span className="text-xs font-medium text-gray-700">
                                Current plan
                              </span>
                            </div>
                            <Link
                              to={`/lesson/${plan.lesson_design_id}`}
                              className="px-3 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors text-xs"
                            >
                              Open
                            </Link>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Subject selection modal for existing stimuli */}
      {subjectModalOpen && subjectModalStimulus && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl shadow-lg max-w-sm w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900">
              Choose a subject for this lesson
            </h3>
            <p className="mt-1 text-sm text-gray-600">
              We&apos;ll use this subject to suggest relevant learning themes from your
              saved stimulus.
            </p>

            <div className="mt-4">
              <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">
                Subject
              </label>
              <select
                value={subjectModalSubject}
                onChange={(e) => setSubjectModalSubject(e.target.value)}
                disabled={subjectModalLoading}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm disabled:bg-gray-100"
              >
                <option value="">Select a subject</option>
                <option value="English">English</option>
                <option value="Music">Music</option>
                <option value="Art">Art</option>
                <option value="History">History</option>
                <option value="Science">Science</option>
                <option value="Mathematics">Mathematics</option>
                <option value="Other">Other</option>
              </select>
            </div>

            {subjectModalError && (
              <div className="mt-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                {subjectModalError}
              </div>
            )}

            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => {
                  if (subjectModalLoading) return
                  setSubjectModalOpen(false)
                  setSubjectModalStimulus(null)
                  setSubjectModalSubject('')
                  setSubjectModalError('')
                }}
                className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubjectModalContinue}
                disabled={subjectModalLoading}
                className="px-4 py-2 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {subjectModalLoading ? 'Analyzing themes…' : 'Continue'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Global blocking overlay while extracting lesson ideas */}
     {isExtracting && (
       <div
         className="fixed inset-0 z-40 flex items-center justify-center bg-black/40"
         aria-modal="true"
         role="dialog"
       >
         <div className="bg-white rounded-2xl shadow-xl max-w-sm w-full mx-4 p-6 text-center">
           <div className="flex justify-center mb-4">
             <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full" />
           </div>
           <h2 className="text-base font-semibold text-gray-900">
             Extracting lesson ideas…
           </h2>
           <p className="mt-2 text-sm text-gray-600">
             This step can take a little while, especially for long PDFs or web
             pages. Please don&apos;t click around, close, or refresh your
             browser until it&apos;s finished.
           </p>
         </div>
       </div>
     )}
   </Layout>
 )
}

export default Dashboard